// src/shared/components/ErrorBoundary.jsx
import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, when: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, when: new Date() };
  }

  componentDidCatch(error, info) {
    // 画面デバッグログにも流す
    try {
      window.__LINE_DBG_PUSH?.({
        type: "error",
        error: String(error),
        stack: error?.stack,
        info: info?.componentStack,
      });
    } catch {}
    this.props.onError?.(error, info);
    this.setState({ info });
  }

  componentDidMount() {
    // 非同期系（Promise拒否/グローバルエラー）も拾う
    this._onWinError = (e) => {
      const err = e?.error || e || new Error("Unknown error");
      this.setState({
        hasError: true,
        error: err,
        info: { componentStack: "[window.error]" },
        when: new Date(),
      });
      try {
        window.__LINE_DBG_PUSH?.({
          type: "error",
          error: String(err),
          stack: err?.stack,
          info: "[window.error]",
        });
      } catch {}
    };
    this._onUnhandled = (e) => {
      const err = e?.reason || e || new Error("Unhandled rejection");
      this.setState({
        hasError: true,
        error: err,
        info: { componentStack: "[unhandledrejection]" },
        when: new Date(),
      });
      try {
        window.__LINE_DBG_PUSH?.({
          type: "error",
          error: String(err),
          stack: err?.stack,
          info: "[unhandledrejection]",
        });
      } catch {}
    };
    window.addEventListener("error", this._onWinError);
    window.addEventListener("unhandledrejection", this._onUnhandled);
  }

  componentWillUnmount() {
    window.removeEventListener("error", this._onWinError);
    window.removeEventListener("unhandledrejection", this._onUnhandled);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, info, when } = this.state;
    const msg = String(error?.message || error || "");
    const stack = String(error?.stack || "");
    const where = info?.componentStack || "";

    const copy = async () => {
      const text =
        `[Error] ${msg}\n\n[Stack]\n${stack}\n\n[Component]\n${where}\n\n` +
        `[URL] ${window.location?.href}\n[UA] ${navigator.userAgent}\n[When] ${when?.toISOString()}`;
      try { await navigator.clipboard?.writeText(text); } catch {}
      alert("エラー詳細をコピーしました。サポートへ貼り付けてください。");
    };

    return (
      <div style={{ padding:16, margin:16, border:"2px solid #ef4444", background:"#fff1f2", color:"#991b1b", fontFamily:"system-ui", borderRadius:12 }}>
        <div style={{ fontWeight:700, marginBottom:8 }}>エラーが発生しました</div>
        <div style={{ color:"#b91c1c", marginBottom:8 }}>{msg}</div>

        {/* スタックと発生箇所 */}
        <pre style={{ whiteSpace:"pre-wrap", fontSize:12, lineHeight:1.4, color:"#7f1d1d", background:"#fff", padding:12, borderRadius:8, border:"1px solid #fca5a5" }}>
{stack}
{where ? `\n--- component stack ---\n${where}` : ""}
        </pre>

        {/* 環境情報 */}
        <div style={{ fontSize:12, color:"#6b7280", marginTop:8 }}>
          URL: {window.location?.href}<br/>
          時刻: {when?.toLocaleString?.() || ""}<br/>
          UA: {navigator.userAgent}
        </div>

        <div style={{ marginTop:12, display:"flex", gap:8, flexWrap:"wrap" }}>
          <button onClick={() => window.location.reload()} style={{ padding:"8px 12px", border:"1px solid #991b1b", background:"#fff", color:"#991b1b", borderRadius:8 }}>
            再読み込み
          </button>
          <button onClick={() => window.history.back()} style={{ padding:"8px 12px", border:"1px solid #991b1b", background:"#fff", color:"#991b1b", borderRadius:8 }}>
            1つ前に戻る
          </button>
          <button onClick={copy} style={{ padding:"8px 12px", border:"1px solid #991b1b", background:"#fff", color:"#991b1b", borderRadius:8 }}>
            詳細をコピー
          </button>
        </div>
      </div>
    );
  }
}
